import { ChangeDetectionStrategy, Component, Injector, Input, OnInit } from '@angular/core';
import { take } from 'rxjs/operators';
import { WidgetBaseComponent } from '../../widgetBase';
import { RdLocalizedNumberPipe } from '../../../pipes/rd-localized-number.pipe';

/**
 * DcfGrowthRateInflationIndex Widget
 */
@Component({
  selector: 'dcf-growth-rate-inflation-index-widget',
  templateUrl: './dcf-growth-rate-inflation-index-widget.component.html',
  styleUrls: ['./dcf-growth-rate-inflation-index-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DcfGrowthRateInflationIndexWidgetComponent extends WidgetBaseComponent implements OnInit {

  /**
   * Widget Guid
   */
  @Input() widgetGuid: string;

  /**
   * Chart categories
   */
  categories: number[];

  /**
   * Are charts ready to be displayed?
   */
  chartsReady = false;

  /**
   * Is widget currently resizing?
   */
  private resizing = false;

  /**
  * Constructor
  * @param rdLocalizedNumberPipe RdLocalizedNumberPipe
  */
  constructor(injector: Injector, private rdLocalizedNumberPipe: RdLocalizedNumberPipe) {
    super(injector, 'dcfGrowthRateInflationIndex', true);
  }

  /**
   * Widget's text area options (if used)
   */
  textAreaOptions: any = null;

  /**
   * Initializes the component
   */
  ngOnInit() {
    // will call registerEvents and onInitialized when config (this.widgetConfig and this.page) is ready

    this.initWidgetConfig();
    this.widgetResized();
  }

  /**
   * Called by base class to let widget register to various app events (like saving, loading, resizing etc)
   */
  registerEvents(): void {
    this.useDefaultServiceSaveChangesHandler("dcfgrowthrateinflationindex", this.provideSaveObject.bind(this));
  }

  /**
   * Called by base class to inform widget that it is initialized
   * Mostly used to define where widget data should be loaded from
   */
  onInitialized(): void {
    this.setupLayouts();

    this.mockupData$ = this.widgetService.loadMockup('dcfgrowthrateinflationindex').pipe(take(1));

    this.widgetData$ = this.mockup ?
      this.mockupData$
      : this.widgetService.load('dcfgrowthrateinflationindex', this.getReport()).pipe();

  }

  /**
   * Setup widget's layout if required
   */
  setupLayouts() {
    const fixLayouts = this.availableLayouts.length === 0 && this.currentLayout === 'default';

    if (fixLayouts) {
      this.currentLayout = 'Large';
    }
  }

  /**
   * Resize event handler
   */
  protected widgetResized(): void {
    if (!this.resizing) {
      this.resizing = true;
      this.chartsReady = false;
      this.ref.markForCheck();
      setTimeout(() => {
        this.chartsReady = true;
        this.resizing = false;
        this.ref.markForCheck();
      });
    }
  }

  /**
   * Layout changed event handler
  * @param newLayout {string} New layout
   */
  public layoutChanged(newLayout: string): void {
    try {
      if (this.widgetConfig?.widgetSettings?.layoutSizeConfiguration) {
        const config = this.widgetConfig.widgetSettings.layoutSizeConfiguration[newLayout];
        if (config.x) {
          this.widgetConfig.sizeX = config.x;

        }
        if (config.y) {
          this.widgetConfig.sizeY = config.y;
        }
      }
      this.widgetConfig.widgetSettings.currentLayout = newLayout;
      this.ref.markForCheck();
    } catch (_e) {
      // Nothing
    }
  }

  /**
   * Called by base class to let widget preprocess loaded data if required
   */
  protected preprocessLoadedData(data) {
    this.categories = [];

    data.rows = [];
    //Growth
    this.addRow(data, 'dcfAssumptionGrowthItem01Label', 'dcfAssumptionGrowthItem01Total', 'dcfAssumptionGrowthItem01Year1', 'dcfAssumptionGrowthItem01Year2', 'dcfAssumptionGrowthItem01Year3To5', 'dcfAssumptionGrowthItem01Year6To10');
    this.addRow(data, 'dcfAssumptionGrowthItem02Label', 'dcfAssumptionGrowthItem02Total', 'dcfAssumptionGrowthItem02Year1', 'dcfAssumptionGrowthItem02Year2', 'dcfAssumptionGrowthItem02Year3To5', 'dcfAssumptionGrowthItem02Year6To10');
    this.addRow(data, 'dcfAssumptionGrowthItem03Label', 'dcfAssumptionGrowthItem03Total', 'dcfAssumptionGrowthItem03Year1', 'dcfAssumptionGrowthItem03Year2', 'dcfAssumptionGrowthItem03Year3To5', 'dcfAssumptionGrowthItem03Year6To10');
    this.addRow(data, 'dcfAssumptionGrowthItem04Label', 'dcfAssumptionGrowthItem04Total', 'dcfAssumptionGrowthItem04Year1', 'dcfAssumptionGrowthItem04Year2', 'dcfAssumptionGrowthItem04Year3To5', 'dcfAssumptionGrowthItem04Year6To10');
    this.addRow(data, 'dcfAssumptionGrowthItem05Label', 'dcfAssumptionGrowthItem05Total', 'dcfAssumptionGrowthItem05Year1', 'dcfAssumptionGrowthItem05Year2', 'dcfAssumptionGrowthItem05Year3To5', 'dcfAssumptionGrowthItem05Year6To10');

    //Cost
    this.addRow(data, 'dcfAssumptionCostItem01Label', 'dcfAssumptionCostItem01Total', 'dcfAssumptionCostItem01Year1', 'dcfAssumptionCostItem01Year2', 'dcfAssumptionCostItem01Year3To5', 'dcfAssumptionCostItem01Year6To10');
    this.addRow(data, 'dcfAssumptionCostItem02Label', 'dcfAssumptionCostItem02Total', 'dcfAssumptionCostItem02Year1', 'dcfAssumptionCostItem02Year2', 'dcfAssumptionCostItem02Year3To5', 'dcfAssumptionCostItem02Year6To10');
    this.addRow(data, 'dcfAssumptionCostItem03Label', 'dcfAssumptionCostItem03Total', 'dcfAssumptionCostItem03Year1', 'dcfAssumptionCostItem03Year2', 'dcfAssumptionCostItem03Year3To5', 'dcfAssumptionCostItem03Year6To10');
    this.addRow(data, 'dcfAssumptionCostItem04Label', 'dcfAssumptionCostItem04Total', 'dcfAssumptionCostItem04Year1', 'dcfAssumptionCostItem04Year2', 'dcfAssumptionCostItem04Year3To5', 'dcfAssumptionCostItem04Year6To10');
    this.addRow(data, 'dcfAssumptionCostItem05Label', 'dcfAssumptionCostItem05Total', 'dcfAssumptionCostItem05Year1', 'dcfAssumptionCostItem05Year2', 'dcfAssumptionCostItem05Year3To5', 'dcfAssumptionCostItem05Year6To10');


    data.chartData = [
      this.addChartSeries(data, 0, 'dcfAssumptionGrowthItem01Label', 'dcfAssumptionGrowthItem01Year'),
      this.addChartSeries(data, 1, 'dcfAssumptionGrowthItem02Label', 'dcfAssumptionGrowthItem02Year'),
      this.addChartSeries(data, 2, 'dcfAssumptionGrowthItem03Label', 'dcfAssumptionGrowthItem03Year'),
      this.addChartSeries(data, 3, 'dcfAssumptionGrowthItem04Label', 'dcfAssumptionGrowthItem04Year'),
      this.addChartSeries(data, 4, 'dcfAssumptionGrowthItem05Label', 'dcfAssumptionGrowthItem05Year'),

      this.addChartSeries(data, 0, 'dcfAssumptionCostItem01Label', 'dcfAssumptionCostItem01Year'),
      this.addChartSeries(data, 1, 'dcfAssumptionCostItem02Label', 'dcfAssumptionCostItem02Year'),
      this.addChartSeries(data, 2, 'dcfAssumptionCostItem03Label', 'dcfAssumptionCostItem03Year'),
      this.addChartSeries(data, 3, 'dcfAssumptionCostItem04Label', 'dcfAssumptionCostItem04Year'),
      this.addChartSeries(data, 4, 'dcfAssumptionCostItem05Label', 'dcfAssumptionCostItem05Year'),
    ];

    for (let i = 1; i <= 10; ++i) {
      this.categories.push(this.getStartDate(data.dcfStartDate, i).getFullYear());
    }

    this.chartsReady = true;
    return data;
  }

  /**
   * Add chart series
  * @param data {any} Data
  * @param index {number} Index
  * @param labelField {string} Label field
  * @param yearField {string} Year field
   */
  private addChartSeries(data: any, index: number, labelField: string, yearField: string): any {
    const ret = {
      name: data[labelField],
      color: this.getColorByIndex(index, 5),
      data: []
    };

    for (let i = 1; i <= 10; ++i) {
      const fieldName = yearField + i;
      const entry = data[fieldName];
      if (entry) {
        ret.data.push(this.rdLocalizedNumberPipe.transform((entry * 100), 2, false, '', '', 0));
      }
      else {
        ret.data.push(null);
      }
    }

    return ret;
  }

  /**
   * Returns label content
  * @param e {any} Event object
   */
  labelContent(e: any): string {
    return `${e.value}%`;
  }

  /**
   * Add data row
  * @param data {any} Data
  * @param labelField {string} Label field
  * @param totalField {string} Total field
  * @param year1Field {string} Year 1 field
  * @param year2Field {string} Year 2 field
  * @param year3To5Field {string} Year 3-5 field
  * @param year6To10Field {string} Year 6-10 field
   */
  private addRow(data: any, labelField: string, totalField: string, year1Field: string, year2Field: string, year3To5Field: string, year6To10Field: string): void {
    try {
      const row = {
        label: data[labelField],
        total: data[totalField],
        year1: data[year1Field],
        year2: data[year2Field],
        year3To5: data[year3To5Field],
        year6To10: data[year6To10Field],
      };
      data.rows.push(row);
    } catch (e) {
      // Nothing
    }
  }

  /**
   * Called by base class in save process. Widget should return object to be saved
   */
  async provideSaveObject(): Promise<any> {
    return this.widgetData;
  }

  /**
   * Called by base class to inform widget about save action result
   * @param _success {boolean} The result of save action
   * @param _err {any} The error object, if save action failed
   */
  saveCompleted(_success: boolean, _err: any = null): void {
    //do something if error
  }

  /**
   * Returns start date
  * @param date {Date} Date
  * @param index {number} Index
   */
  getStartDate(date: Date, index: number): Date {
    const newDate = new Date(date)
    newDate.setFullYear(newDate.getFullYear() + index - 1)
    return newDate;
  }

  /**
   * Returns end date
  * @param date {Date} Date
  * @param index {number} Index
   */
  getEndDate(date: Date, index: number): Date {
    const newDate = new Date(date);
    const year = newDate.getFullYear() + index;
    newDate.setDate(newDate.getDate() - 1);
    newDate.setFullYear(year);
    if (this.leapYear(year) && newDate.getMonth() === 1) {
      newDate.setDate(29);
    }
    return newDate;
  }

  /**
   * Checks if year is leap year
  * @param year {any} Year
   */
  leapYear(year): boolean {
    return ((year % 4 == 0) && (year % 100 != 0)) || (year % 400 == 0);
  }
}

